import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { PieChartComponent } from '../../chart/pie-chart/pie-chart.component';
import { ExecutionCycle } from '../execution-cycle-card/execution-cycle-card.component';

/** Colour palette aligned with MultiSegmentProgressBar and StatusBadge */
const STATUS_COLORS: Record<string, string> = {
  passed:    '#02F576',
  failed:    '#FC404A',
  executing: '#DED6FF',
  waiting:   '#E8E8E9',
};

@Component({
  selector: 'app-result-summary',
  standalone: true,
  imports: [CommonModule, PieChartComponent],
  templateUrl: './result-summary.component.html',
  styleUrl: './result-summary.component.scss'
})
export class ResultSummaryComponent implements OnChanges {
  @Input() executionCycles: ExecutionCycle[] = [];
  @Input() isLoading: boolean = false;
  /**
   * Optional pre-aggregated summary from the BE (shape: { PASS, FAIL, PENDING, ABORTED, EXECUTING? }).
   * When provided this takes priority over summing individual executionCycles.
   */
  @Input() summary: { PASS?: number; FAIL?: number; PENDING?: number; ABORTED?: number; EXECUTING?: number } | null = null;

  totalPassed    = 0;
  totalFailed    = 0;
  totalExecuting = 0;
  totalWaiting   = 0;
  grandTotal     = 1;

  chartData: any = {};
  chartMeta: any = {
    backgroundColors:       [],
    showLabels:             false,
    cutout:                 '65%',
    showPercentageInLegend: false,
    isLegendVisible:        false,
  };

  readonly statCards = [
    { key: 'passed',    label: 'Passed',    icon: 'assets/images/virtusa-icons/passed-icon.svg',  cssClass: 'rs-passed'    },
    { key: 'failed',    label: 'Failed',    icon: 'assets/images/virtusa-icons/failed-icon.svg',  cssClass: 'rs-failed'    },
    { key: 'executing', label: 'Executing', icon: 'assets/images/virtusa-icons/executing.svg',    cssClass: 'rs-executing' },
    { key: 'waiting',   label: 'Waiting',   icon: 'assets/images/virtusa-icons/waiting.svg',      cssClass: 'rs-waiting'   },
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['executionCycles'] || changes['summary']) {
      this.computeSummary();
    }
  }

  getCount(key: string): number {
    switch (key) {
      case 'passed':    return this.totalPassed;
      case 'failed':    return this.totalFailed;
      case 'executing': return this.totalExecuting;
      case 'waiting':   return this.totalWaiting;
      default:          return 0;
    }
  }

  getPercent(count: number): number {
    return Math.round((count / this.grandTotal) * 100);
  }

  private computeSummary(): void {
    if (this.summary) {
      this.totalPassed    = this.summary.PASS      ?? 0;
      this.totalFailed    = this.summary.FAIL      ?? 0;
      this.totalExecuting = this.summary.EXECUTING ?? 0;
      this.totalWaiting   = this.summary.PENDING   ?? 0;
    } else {
      this.totalPassed    = this.sum('passedCount');
      this.totalFailed    = this.sum('failedCount');
      this.totalExecuting = this.sum('executingCount');
      this.totalWaiting   = this.sum('waitingCount');
    }
    this.grandTotal = (this.totalPassed + this.totalFailed + this.totalExecuting + this.totalWaiting) || 1;

    const rawData: Record<string, number> = {
      passed:    this.totalPassed,
      failed:    this.totalFailed,
      executing: this.totalExecuting,
      waiting:   this.totalWaiting,
    };

    // Only include non-zero slices (mirrors getOverallTestResultSummary pattern)
    const filteredData: Record<string, number> = {};
    const filteredColors: string[] = [];
    Object.entries(rawData).forEach(([key, val]) => {
      if (val > 0) {
        filteredData[key] = val;
        filteredColors.push(STATUS_COLORS[key]);
      }
    });

    this.chartData = filteredData;
    this.chartMeta = { ...this.chartMeta, backgroundColors: filteredColors };
  }

  private sum(field: keyof ExecutionCycle): number {
    return this.executionCycles.reduce((acc, c) => acc + ((c[field] as number) || 0), 0);
  }
}
